import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Loader2, FileSpreadsheet, FileJson, AlertCircle,
  CheckCircle2, XCircle, ExternalLink, RefreshCw, Play, ClipboardCopy,
  Link2, Trash2, Clock,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { useState, useMemo, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { projectsApi, dataBrowserApi, serpApi, tasksApi, type Project } from "@/api";
import { alibabaApi, type AlibabaProduct } from "@/api/alibaba";
import { serpAnalysisApi, type AnalysisListItem } from "@/api/serp-analysis";
import { MultiSelectFilter, type MultiSelectOption } from "@/components/MultiSelectFilter";

interface SerpResultRow {
  result_id: string;
  request_id: string;
  rank_absolute: number;
  rank_group?: number;
  domain?: string;
  title?: string;
  url: string;
  snippet?: string;
  item_type?: string;
  scraping_task_id?: string;
  target_keyword?: string;
  scrape_status?: string;
  scrape_completed_at?: string;
  word_count?: number;
  overall_score?: number;
  page_title?: string;
  page_meta?: string;
}

export default function ProjectExport() {
  const [searchParams, setSearchParams] = useSearchParams();
  const exportTab = (['serp','alibaba','analysis'] as const).includes(searchParams.get('tab') as 'serp') ? searchParams.get('tab') as string : 'serp';
  const setExportTab = (t: string) => setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('tab', t); return p; }, { replace: true });
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [selectedKeywords, setSelectedKeywords] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState<"excel" | "json" | "copy" | false>(false);

  // Export content options — all default ON
  const [includeMarkdown, setIncludeMarkdown] = useState(true);
  const [includeHtml, setIncludeHtml] = useState(true);
  const [includeSeo, setIncludeSeo] = useState(true);

  // ── SERP Analysis Export state ──
  const [analysisKeywords, setAnalysisKeywords] = useState<AnalysisListItem[]>([]);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisFormat, setAnalysisFormat] = useState<'excel' | 'json'>('excel');
  const [requireAllSteps, setRequireAllSteps] = useState(true);
  const [analysisExporting, setAnalysisExporting] = useState(false);
  const [analysisSheetUrl, setAnalysisSheetUrl] = useState(() => localStorage.getItem('app04_last_sheet_url') || '');
  const [analysisSheetTab, setAnalysisSheetTab] = useState(() => localStorage.getItem('app04_last_sheet_tab') || 'SERP Analysis');
  const [analysisSheetExporting, setAnalysisSheetExporting] = useState(false);

  const queryClient = useQueryClient();

  // Fetch projects
  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ["projects"],
    queryFn: async () => {
      const r = await projectsApi.list();
      return r.data || [];
    },
  });

  // Fetch ALL SERP results for selected project
  const { data: allProjectData = [], isLoading } = useQuery<SerpResultRow[]>({
    queryKey: ["project-export-data", selectedProjectId],
    queryFn: async () => {
      if (!selectedProjectId) return [];
      const r = await dataBrowserApi.serpResults({
        project_id: selectedProjectId,
        limit: 500,
      });
      return (r.data as SerpResultRow[]) || [];
    },
    enabled: !!selectedProjectId,
  });

  // Build keyword options with counts from SERP results
  const keywordOptions: MultiSelectOption[] = useMemo(() => {
    const counts = new Map<string, number>();
    allProjectData.forEach(item => {
      const kw = item.target_keyword;
      if (kw) counts.set(kw, (counts.get(kw) || 0) + 1);
    });
    return Array.from(counts.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([kw, count]) => ({ value: kw, label: kw, count }));
  }, [allProjectData]);

  // Client-side keyword filtering — empty Set = show all
  const filteredData = selectedKeywords.size > 0
    ? allProjectData.filter(item => item.target_keyword != null && selectedKeywords.has(item.target_keyword))
    : allProjectData;

  // Statistics
  const stats = {
    total: filteredData.length,
    top20: filteredData.filter(item => item.rank_absolute <= 20).length,
    scraped: filteredData.filter(item => !!item.scraping_task_id && item.scrape_status === 'completed').length,
    failed: filteredData.filter(item => item.scrape_status === 'failed').length,
    keywords: Array.from(new Set(filteredData.map(item => item.target_keyword))).length,
  };

  // Single retry mutation
  const retryMutation = useMutation({
    mutationFn: (taskId: string) => tasksApi.retry(taskId),
    onSuccess: () => {
      toast.success("已重新排入爬取佇列");
      queryClient.invalidateQueries({ queryKey: ["project-export-data"] });
    },
    onError: (err: Error) => toast.error(`重試失敗: ${err.message}`),
  });

  // Batch retry failed
  const batchRetryMutation = useMutation({
    mutationFn: (taskIds: string[]) => tasksApi.batchRetry(taskIds),
    onSuccess: () => {
      toast.success("所有失敗任務已重新排入佇列");
      queryClient.invalidateQueries({ queryKey: ["project-export-data"] });
    },
    onError: (err: Error) => toast.error(`批次重試失敗: ${err.message}`),
  });

  // Trigger scrape for unscraped URL
  const scrapeMutation = useMutation({
    mutationFn: (data: { url: string; keyword?: string; project_id?: string }) =>
      tasksApi.create({ url: data.url, keyword: data.keyword, project_id: data.project_id }),
    onSuccess: () => {
      toast.success("已建立爬取任務");
      queryClient.invalidateQueries({ queryKey: ["project-export-data"] });
    },
    onError: (err: Error) => toast.error(`建立爬取失敗: ${err.message}`),
  });

  // Re-scrape entire project
  const rescrapeProjectMutation = useMutation({
    mutationFn: (projectId: string) => tasksApi.rescrapeProject(projectId),
    onSuccess: (res) => {
      const data = res.data as { total: number; queued: number } | undefined;
      queryClient.invalidateQueries({ queryKey: ["project-export-data"] });
      if (data) {
        toast.success(`已將 ${data.queued}/${data.total} 個任務重新排入爬取佇列`);
      } else {
        toast.success("已重新排入佇列");
      }
    },
    onError: (err: Error) => toast.error(`重新爬取失敗: ${err.message}`),
  });

  const selectedProject = projects.find(p => p.project_id === selectedProjectId);

  // Load analysis keywords for the selected project
  const loadAnalysisKeywords = useCallback(async () => {
    if (!selectedProjectId) return;
    setAnalysisLoading(true);
    try {
      const res = await serpAnalysisApi.list({ project_id: selectedProjectId, limit: 200 });
      setAnalysisKeywords(res.data?.items ?? []);
    } catch (e) {
      console.error('Failed to load analysis keywords', e);
      setAnalysisKeywords([]);
    } finally {
      setAnalysisLoading(false);
    }
  }, [selectedProjectId]);

  // Reload analysis keywords when switching to analysis tab or when project changes
  useEffect(() => {
    if (exportTab === 'analysis' && selectedProjectId) {
      loadAnalysisKeywords();
    }
  }, [exportTab, selectedProjectId, loadAnalysisKeywords]);

  // Download analysis bulk export
  const handleAnalysisExport = async () => {
    if (!selectedProjectId) return;
    setAnalysisExporting(true);
    try {
      const blob = await serpAnalysisApi.bulkExport(selectedProjectId, {
        format: analysisFormat,
        requireAllSteps,
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      const safeName = (selectedProject?.name || selectedProjectId.slice(0, 8)).replace(/[^a-zA-Z0-9一-鿿_-]/g, '_');
      const ext = analysisFormat === 'json' ? 'json' : 'xlsx';
      link.download = `SERP_Analysis_${safeName}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      toast.success(`分析報告 ${ext.toUpperCase()} 已下載`);
    } catch (err: any) {
      toast.error(`匯出失敗: ${err.message || '請稍後再試'}`);
    } finally {
      setAnalysisExporting(false);
    }
  };

  const handleExportAnalysisToSheets = async () => {
    if (!selectedProjectId) { toast.error("請先選擇專案"); return; }
    if (!analysisSheetUrl.trim()) { toast.error("請輸入 Google Sheets 網址"); return; }
    if (!sheetsTokens) { toast.error("請先連結 Google Sheets"); return; }
    setAnalysisSheetExporting(true);
    try {
      const res = await alibabaApi.exportAnalysisToSheets({
        projectId: selectedProjectId,
        sheetUrl: analysisSheetUrl.trim(),
        tabName: analysisSheetTab.trim() || 'SERP Analysis',
        requireAllSteps,
        accessToken: sheetsTokens.access_token,
        refreshToken: sheetsTokens.refresh_token,
      });
      toast.success(`已成功寫入 ${res.data?.rows ?? 0} 筆到「${res.data?.tabName}」分頁`);
      localStorage.setItem('app04_last_sheet_url', analysisSheetUrl.trim());
      localStorage.setItem('app04_last_sheet_tab', analysisSheetTab.trim() || 'SERP Analysis');
      window.open(analysisSheetUrl.trim(), '_blank');
    } catch (error: any) {
      if (error.code === 'TOKEN_EXPIRED' || error.message?.includes('token')) {
        setSheetsAuthorized(false);
        setSheetsTokens(null);
        toast.error('授權過期，請重新連結 Google Sheets');
      } else {
        toast.error(`匯出失敗: ${error.message || '請稍後再試'}`);
      }
    } finally {
      setAnalysisSheetExporting(false);
    }
  };

  const handleBatchRetryFailed = () => {
    const failedIds = filteredData
      .filter(item => item.scrape_status === 'failed' && item.scraping_task_id)
      .map(item => item.scraping_task_id!);
    if (failedIds.length === 0) {
      toast.info("沒有失敗的爬取任務");
      return;
    }
    batchRetryMutation.mutate(failedIds);
  };

  // Download export file
  const handleExport = async (format: "excel" | "json") => {
    if (!selectedProjectId) { toast.error("請先選擇專案"); return; }
    setIsExporting(format);
    try {
      const keywordParam = selectedKeywords.size > 0 ? [...selectedKeywords].join(',') : undefined;
      const blob = await serpApi.exportProject(selectedProjectId, {
        keyword: keywordParam,
        format,
        include_markdown: includeMarkdown,
        include_html: includeHtml,
        include_seo: includeSeo,
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      const safeName = (selectedProject?.name || selectedProjectId.slice(0, 8)).replace(/[^a-zA-Z0-9\u4e00-\u9fff_-]/g, '_');
      const ext = format === 'json' ? 'json' : 'xlsx';
      link.download = `SERP_${safeName}_export.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      toast.success(`${ext.toUpperCase()} 檔案已下載`);
    } catch (error: any) {
      toast.error(`匯出失敗: ${error.message || "請稍後再試"}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Copy JSON to clipboard
  const handleCopyJson = async () => {
    if (!selectedProjectId) { toast.error("請先選擇專案"); return; }
    setIsExporting("copy");
    try {
      const keywordParam = selectedKeywords.size > 0 ? [...selectedKeywords].join(',') : undefined;
      const blob = await serpApi.exportProject(selectedProjectId, {
        keyword: keywordParam,
        format: "json",
        include_markdown: includeMarkdown,
        include_html: includeHtml,
        include_seo: includeSeo,
      });
      const text = await blob.text();
      // navigator.clipboard.writeText requires document focus; after async fetch
      // the window may lose focus, so use textarea fallback
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success("JSON 已複製到剪貼簿");
    } catch (error: any) {
      toast.error(`複製失敗: ${error.message || "請稍後再試"}`);
    } finally {
      setIsExporting(false);
    }
  };

  // ── Alibaba Export state ──
  const [aliKeyword, setAliKeyword] = useState("");
  const [aliExportType, setAliExportType] = useState<"search_results" | "product_details" | "full_details">("full_details");
  const [aliFormat, setAliFormat] = useState<"csv" | "excel" | "json">("excel");
  const [aliExporting, setAliExporting] = useState(false);

  // ── Google Sheets state (persist tokens in localStorage) ──
  const [sheetsExporting, setSheetsExporting] = useState(false);
  const [sheetsTokens, setSheetsTokens] = useState<{ access_token: string; refresh_token?: string } | null>(() => {
    try {
      const saved = localStorage.getItem('google_sheets_tokens');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const sheetsAuthorized = !!sheetsTokens;
  const setSheetsAuthorized = (v: boolean) => {
    if (!v) {
      setSheetsTokens(null);
      localStorage.removeItem('google_sheets_tokens');
    }
  };

  // ── Google Sheet Binding state ──
  const [bindingUrl, setBindingUrl] = useState("");
  const [bindingKeyword, setBindingKeyword] = useState("");
  const [bindingInterval, setBindingInterval] = useState("24");
  const [bindingLoading, setBindingLoading] = useState(false);

  const { data: bindings = [], refetch: refetchBindings } = useQuery({
    queryKey: ["sheet-bindings"],
    queryFn: async () => (await alibabaApi.listBindings()).data || [],
  });

  const syncBindingMut = useMutation({
    mutationFn: (id: number) => alibabaApi.syncBinding(id),
    onSuccess: (res, id) => {
      const rows = (res as any)?.data?.rows ?? 0;
      toast.success(`同步成功：${rows} 筆`);
      refetchBindings();
    },
    onError: (e: any) => toast.error(`同步失敗：${e.message}`),
  });

  const deleteBindingMut = useMutation({
    mutationFn: (id: number) => alibabaApi.deleteBinding(id),
    onSuccess: () => { toast.success("已刪除綁定"); refetchBindings(); },
    onError: (e: any) => toast.error(`刪除失敗：${e.message}`),
  });

  const handleBindSheet = async () => {
    if (!bindingUrl) { toast.error("請輸入 Google Sheet URL"); return; }
    if (!bindingKeyword) { toast.error("請選擇關鍵字"); return; }
    if (!sheetsTokens) { toast.error("請先連結 Google Sheets"); return; }
    setBindingLoading(true);
    try {
      const res = await alibabaApi.bindSheet({
        sheet_url: bindingUrl,
        keyword: bindingKeyword,
        sync_interval_hours: parseInt(bindingInterval),
        access_token: sheetsTokens.access_token,
        refresh_token: sheetsTokens.refresh_token,
      });
      const newId = (res as any)?.data?.id;
      toast.success("綁定成功，正在立即同步…");
      if (newId) {
        await alibabaApi.syncBinding(newId);
        toast.success("首次同步完成！");
      }
      setBindingUrl("");
      setBindingKeyword("");
      refetchBindings();
    } catch (e: any) {
      toast.error(`綁定失敗：${e.message}`);
    } finally {
      setBindingLoading(false);
    }
  };

  const { data: aliKeywords = [] } = useQuery<string[]>({
    queryKey: ["alibaba-keywords"],
    queryFn: async () => (await alibabaApi.keywords()).data || [],
  });

  // Fetch data-viewer keyword stats for selected keyword
  const { data: aliKeywordStats } = useQuery({
    queryKey: ["alibaba-dv-keywords"],
    queryFn: async () => {
      const res = await alibabaApi.dataViewerKeywords();
      return (res.data || []) as Array<{ keyword: string; product_count: number; profile_count: number }>;
    },
  });

  const selectedAliStats = aliKeywordStats?.find(k => k.keyword === aliKeyword);

  // Preview: fetch first few products for selected keyword
  const { data: aliPreview = [], isLoading: aliPreviewLoading } = useQuery<AlibabaProduct[]>({
    queryKey: ["alibaba-preview", aliKeyword],
    queryFn: async () => {
      if (!aliKeyword) return [];
      const res = await alibabaApi.searches({ keyword: aliKeyword, limit: 1 });
      const searches = res.data || [];
      if (searches.length === 0) return [];
      const detail = await alibabaApi.getSearch(searches[0].search_id);
      return (detail.data?.products || []).slice(0, 5);
    },
    enabled: !!aliKeyword,
  });

  const handleAliExport = async () => {
    if (!aliKeyword) { toast.error("請先選擇關鍵字"); return; }
    setAliExporting(true);
    try {
      const blob = await alibabaApi.exportData({
        keyword: aliKeyword,
        type: aliExportType,
        format: aliFormat,
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      const ext = aliFormat === 'excel' ? 'xlsx' : aliFormat === 'json' ? 'json' : 'csv';
      const typeLabel = aliExportType === 'full_details' ? 'full' : aliExportType === 'product_details' ? 'details' : 'search';
      link.download = `alibaba-${typeLabel}-${aliKeyword.replace(/\s+/g, '_')}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      toast.success(`${ext.toUpperCase()} 檔案已下載`);
    } catch (error: any) {
      toast.error(`匯出失敗: ${error.message || "請稍後再試"}`);
    } finally {
      setAliExporting(false);
    }
  };

  // ── Google Sheets handlers ──
  const handleSheetsAuth = async () => {
    try {
      const res = await alibabaApi.getSheetsAuthUrl();
      const authUrl = res.data;
      if (!authUrl) { toast.error("無法取得授權 URL"); return; }

      const popup = window.open(authUrl, 'google-sheets-auth', 'width=600,height=700');

      const handler = (event: MessageEvent) => {
        if (event.data?.type === 'google-sheets-auth') {
          window.removeEventListener('message', handler);
          if (event.data.error) {
            toast.error(`Google 授權失敗: ${event.data.error}`);
            return;
          }
          const tokens = event.data.tokens;
          setSheetsTokens(tokens);
          try { localStorage.setItem('google_sheets_tokens', JSON.stringify(tokens)); } catch { /* */ }
          popup?.close();
          toast.success('Google Sheets 授權成功');
        }
      };
      window.addEventListener('message', handler);
    } catch (error: any) {
      toast.error(`授權失敗: ${error.message}`);
    }
  };

  const handleExportToSheets = async () => {
    if (!aliKeyword || !sheetsTokens) return;
    setSheetsExporting(true);
    try {
      const res = await alibabaApi.exportToSheets({
        keyword: aliKeyword,
        accessToken: sheetsTokens.access_token,
        refreshToken: sheetsTokens.refresh_token,
      });
      if (res.data?.url) {
        window.open(res.data.url, '_blank');
        toast.success(`Google Sheets 已建立（${res.data.rowCount} 筆），已開啟新分頁`);
      }
    } catch (error: any) {
      if (error.code === 'TOKEN_EXPIRED' || error.message?.includes('token')) {
        setSheetsAuthorized(false);
        setSheetsTokens(null);
        toast.error('授權過期，請重新連結 Google Sheets');
      } else {
        toast.error(`建立失敗: ${error.message}`);
      }
    } finally {
      setSheetsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">專案匯出</h1>
        <p className="text-muted-foreground">匯出 SERP 結果或阿里巴巴搜尋資料</p>
      </div>

      <Tabs value={exportTab} onValueChange={setExportTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="serp">SERP 資料</TabsTrigger>
          <TabsTrigger value="alibaba">阿里巴巴 Alibaba</TabsTrigger>
          <TabsTrigger value="analysis">SERP 分析報告</TabsTrigger>
        </TabsList>

        <TabsContent value="serp" className="space-y-6">

      {/* Project Selection */}
      <Card>
        <CardHeader>
          <CardTitle>選擇專案</CardTitle>
          <CardDescription>選擇要查看和匯出的專案</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>專案</Label>
              <Select
                value={selectedProjectId}
                onValueChange={(value) => {
                  setSelectedProjectId(value);
                  setSelectedKeywords(new Set());
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="選擇專案" />
                </SelectTrigger>
                <SelectContent>
                  {projects.length === 0 ? (
                    <SelectItem value="__none__" disabled>尚無專案</SelectItem>
                  ) : (
                    projects.map(p => (
                      <SelectItem key={p.project_id} value={p.project_id}>
                        {p.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>關鍵字篩選（可選）</Label>
              <MultiSelectFilter
                label="關鍵字"
                options={keywordOptions}
                selected={selectedKeywords}
                onChange={setSelectedKeywords}
              />
            </div>
          </div>

          {selectedProject && (
            <div className="p-4 bg-muted rounded-lg">
              <div className="grid gap-2 md:grid-cols-2">
                <div>
                  <Label className="text-muted-foreground">專案名稱</Label>
                  <p className="font-medium">{selectedProject.name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">專案描述</Label>
                  <p className="text-sm">{selectedProject.description || "無描述"}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statistics */}
      {selectedProjectId && (
        <div className="grid gap-4 md:grid-cols-5">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{stats.total}</div>
              <p className="text-xs text-muted-foreground">總結果數</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-primary">{stats.top20}</div>
              <p className="text-xs text-muted-foreground">Top 20 結果</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-green-600">{stats.scraped}</div>
              <p className="text-xs text-muted-foreground">已爬取成功</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-red-500">{stats.failed}</div>
              <p className="text-xs text-muted-foreground">爬取失敗</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{stats.keywords}</div>
              <p className="text-xs text-muted-foreground">關鍵字數</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Export Options */}
      {selectedProjectId && (
        <Card>
          <CardHeader>
            <CardTitle>匯出設定</CardTitle>
            <CardDescription>勾選要包含的內容，直接點擊下方按鈕匯出</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Content options */}
            <div className="grid gap-4 md:grid-cols-3">
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <div className="text-sm font-medium">SEO 分析資料</div>
                  <div className="text-xs text-muted-foreground">SEO 分數、H 標籤統計</div>
                </div>
                <Switch checked={includeSeo} onCheckedChange={setIncludeSeo} />
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <div className="text-sm font-medium">Markdown 內容</div>
                  <div className="text-xs text-muted-foreground">解析後的純文字內容</div>
                </div>
                <Switch checked={includeMarkdown} onCheckedChange={setIncludeMarkdown} />
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <div className="text-sm font-medium">HTML 原始碼</div>
                  <div className="text-xs text-muted-foreground">完整 HTML（檔案較大）</div>
                </div>
                <Switch checked={includeHtml} onCheckedChange={setIncludeHtml} />
              </div>
            </div>

            {/* Direct action buttons */}
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                onClick={() => handleExport("excel")}
                disabled={!!isExporting || filteredData.length === 0}
                size="lg"
              >
                {isExporting === "excel" ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                )}
                下載 Excel
                {selectedKeywords.size > 0 ? `（${selectedKeywords.size} 個關鍵字）` : ""}
              </Button>
              <Button
                variant="outline"
                onClick={() => handleExport("json")}
                disabled={!!isExporting || filteredData.length === 0}
                size="lg"
              >
                {isExporting === "json" ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <FileJson className="h-4 w-4 mr-2" />
                )}
                下載 JSON
              </Button>
              <Button
                variant="outline"
                onClick={handleCopyJson}
                disabled={!!isExporting || filteredData.length === 0}
                size="lg"
              >
                {isExporting === "copy" ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <ClipboardCopy className="h-4 w-4 mr-2" />
                )}
                複製 JSON
              </Button>

              <div className="border-l pl-3 ml-1 flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => rescrapeProjectMutation.mutate(selectedProjectId)}
                  disabled={rescrapeProjectMutation.isPending || filteredData.length === 0}
                >
                  {rescrapeProjectMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  重新爬取整個專案
                </Button>

                {stats.failed > 0 && (
                  <Button
                    variant="outline"
                    onClick={handleBatchRetryFailed}
                    disabled={batchRetryMutation.isPending}
                  >
                    {batchRetryMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    重爬所有失敗（{stats.failed}）
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results Table */}
      {selectedProjectId && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>專案 SERP 結果</CardTitle>
                <CardDescription>
                  {selectedKeywords.size > 0 ? `已篩選 ${selectedKeywords.size} 個關鍵字` : "所有關鍵字的 SERP 結果"}
                  {filteredData.length > 0 && `（共 ${filteredData.length} 筆）`}
                </CardDescription>
              </div>
              {filteredData.length > 0 && (
                <Badge variant="secondary">
                  {filteredData.length} 筆結果
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground">載入中...</p>
              </div>
            ) : filteredData.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <AlertCircle className="h-8 w-8 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  {selectedProjectId ? "尚無結果資料" : "請先選擇專案"}
                </p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-20">關鍵字</TableHead>
                        <TableHead className="w-16 text-center">排名</TableHead>
                        <TableHead className="w-24 text-center">Top 20</TableHead>
                        <TableHead className="min-w-[150px]">網域</TableHead>
                        <TableHead className="min-w-[250px]">標題</TableHead>
                        <TableHead className="min-w-[300px]">摘要</TableHead>
                        <TableHead className="w-28 text-center">爬取狀態</TableHead>
                        <TableHead className="min-w-[200px]">URL</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredData.map((item, idx) => {
                        const isScraped = item.scrape_status === 'completed';
                        const isFailed = item.scrape_status === 'failed';
                        const isPending = item.scrape_status === 'pending' || item.scrape_status === 'processing';
                        const isUnscraped = !item.scraping_task_id;
                        const isTop20 = item.rank_absolute <= 20;
                        return (
                          <TableRow key={`${item.result_id}-${idx}`} className="hover:bg-muted/50">
                            <TableCell className="font-medium">
                              <span className="truncate block max-w-[150px]" title={item.target_keyword}>
                                {item.target_keyword || "—"}
                              </span>
                            </TableCell>
                            <TableCell className="font-bold text-primary text-center">
                              {item.rank_absolute}
                            </TableCell>
                            <TableCell className="text-center">
                              {isTop20 ? (
                                <Badge variant="default" className="bg-primary">Top 20</Badge>
                              ) : (
                                <span className="text-muted-foreground text-xs">—</span>
                              )}
                            </TableCell>
                            <TableCell className="font-medium">
                              <span className="truncate block max-w-[150px]" title={item.domain}>
                                {item.domain || "—"}
                              </span>
                            </TableCell>
                            <TableCell className="max-w-[300px]">
                              <div className="line-clamp-2 font-medium" title={item.title}>
                                {item.title || "—"}
                              </div>
                              {isScraped && item.page_title && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  <span className="font-medium">Meta Title:</span> {item.page_title}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="max-w-[300px]">
                              <div className="line-clamp-3 text-sm text-muted-foreground" title={item.snippet}>
                                {item.snippet || "—"}
                              </div>
                              {isScraped && item.page_meta && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  <span className="font-medium">Meta Desc:</span> {item.page_meta}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex flex-col items-center gap-1">
                                {isScraped && (
                                  <>
                                    <Badge variant="default" className="bg-green-500 hover:bg-green-600 text-white text-xs">
                                      <CheckCircle2 className="h-3 w-3 mr-1" />
                                      已爬取
                                    </Badge>
                                    <Link
                                      to={`/tasks/${item.scraping_task_id}`}
                                      className="text-xs text-primary hover:underline flex items-center gap-1"
                                    >
                                      <ExternalLink className="h-3 w-3" />
                                      查看
                                    </Link>
                                  </>
                                )}
                                {isFailed && (
                                  <>
                                    <Badge variant="destructive" className="text-xs">
                                      <XCircle className="h-3 w-3 mr-1" />
                                      失敗
                                    </Badge>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 text-xs px-2"
                                      disabled={retryMutation.isPending}
                                      onClick={() => retryMutation.mutate(item.scraping_task_id!)}
                                    >
                                      <RefreshCw className="h-3 w-3 mr-1" />
                                      重爬
                                    </Button>
                                  </>
                                )}
                                {isPending && (
                                  <Badge variant="outline" className="text-xs">
                                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                    處理中
                                  </Badge>
                                )}
                                {isUnscraped && (
                                  <>
                                    <Badge variant="secondary" className="text-xs">
                                      <XCircle className="h-3 w-3 mr-1" />
                                      未爬取
                                    </Badge>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 text-xs px-2"
                                      disabled={scrapeMutation.isPending}
                                      onClick={() => scrapeMutation.mutate({
                                        url: item.url,
                                        keyword: item.target_keyword,
                                        project_id: selectedProjectId,
                                      })}
                                    >
                                      <Play className="h-3 w-3 mr-1" />
                                      爬取
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              <a
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:underline flex items-center gap-1 group"
                                title={item.url}
                              >
                                <span className="truncate max-w-[180px] group-hover:text-primary/80">
                                  {item.url || "—"}
                                </span>
                                <ExternalLink className="h-3 w-3 flex-shrink-0 opacity-60 group-hover:opacity-100" />
                              </a>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

        </TabsContent>

        {/* ── Alibaba Export Tab ── */}
        <TabsContent value="alibaba" className="space-y-6">

          {/* Keyword Selection */}
          <Card>
            <CardHeader>
              <CardTitle>Alibaba Export 阿里巴巴匯出</CardTitle>
              <CardDescription>匯出阿里巴巴搜尋結果或產品詳情</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Keyword 關鍵字</Label>
                  <Select value={aliKeyword} onValueChange={setAliKeyword}>
                    <SelectTrigger>
                      <SelectValue placeholder="選擇關鍵字" />
                    </SelectTrigger>
                    <SelectContent>
                      {aliKeywords.length === 0 ? (
                        <SelectItem value="__none__" disabled>尚無搜尋紀錄</SelectItem>
                      ) : (
                        aliKeywords.map(kw => (
                          <SelectItem key={kw} value={kw}>{kw}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Export Type 匯出類型</Label>
                  <Select value={aliExportType} onValueChange={(v) => setAliExportType(v as typeof aliExportType)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_details">Full Details 完整明細（含動態屬性）</SelectItem>
                      <SelectItem value="search_results">Search Results 搜尋結果</SelectItem>
                      <SelectItem value="product_details">Product Details 產品詳情</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Format 格式</Label>
                  <Select value={aliFormat} onValueChange={(v) => setAliFormat(v as typeof aliFormat)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="excel">Excel (XLSX) — 含格式化</SelectItem>
                      <SelectItem value="csv">CSV</SelectItem>
                      <SelectItem value="json">JSON</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <Button
                  onClick={handleAliExport}
                  disabled={aliExporting || !aliKeyword}
                  size="lg"
                >
                  {aliExporting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : aliFormat === 'json' ? (
                    <FileJson className="h-4 w-4 mr-2" />
                  ) : (
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                  )}
                  下載 {aliFormat === 'excel' ? 'XLSX' : aliFormat.toUpperCase()}
                </Button>

                <div className="border-l pl-3 ml-1">
                  {!sheetsAuthorized ? (
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={handleSheetsAuth}
                      disabled={!aliKeyword}
                    >
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      連結 Google Sheets
                    </Button>
                  ) : (
                    <Button
                      onClick={handleExportToSheets}
                      disabled={sheetsExporting || !aliKeyword}
                      size="lg"
                      className="bg-[#0F9D58] hover:bg-[#0B8043] text-white"
                    >
                      {sheetsExporting ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <FileSpreadsheet className="h-4 w-4 mr-2" />
                      )}
                      匯出到 Google Sheets
                    </Button>
                  )}
                </div>

                <div className="text-sm text-muted-foreground space-y-1">
                  {aliExportType === 'full_details' ? (
                    <>
                      <p>含：產品名、公司、國家、年資、價格、MOQ、TA、品牌、URL</p>
                      <p>含：圖片1~6（網址）</p>
                      <p>含：全部動態屬性（依覆蓋率排序）+ 覆蓋率統計行</p>
                      {aliFormat === 'excel' && <p>Excel 含最多 4 個 Sheet：摘要 + 產品資料 + 屬性覆蓋率 + 洞察覆蓋率（有資料時）</p>}
                    </>
                  ) : aliExportType === 'search_results' ? (
                    <p>含：關鍵字、頁碼、排序、標題、價格、MOQ、供應商、URL</p>
                  ) : (
                    <p>含：產品名、公司、國家、價格、MOQ、認證、品牌、解析狀態</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Keyword Stats */}
          {aliKeyword && selectedAliStats && (
            <div className="grid gap-4 md:grid-cols-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="text-xl font-bold">{selectedAliStats.product_count}</div>
                  <p className="text-xs text-muted-foreground">產品數 Products</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-xl font-bold">{selectedAliStats.profile_count}</div>
                  <p className="text-xs text-muted-foreground">已解析 Parsed</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-xl font-bold text-green-600">
                    {selectedAliStats.profile_count > 0
                      ? `${Math.round((selectedAliStats.profile_count / selectedAliStats.product_count) * 100)}%`
                      : '0%'}
                  </div>
                  <p className="text-xs text-muted-foreground">解析率 Parse Rate</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-xl font-bold text-blue-600">
                    {selectedAliStats.product_count - selectedAliStats.profile_count}
                  </div>
                  <p className="text-xs text-muted-foreground">未解析 Unparsed</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Preview */}
          {aliKeyword && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Preview 預覽（前 5 筆搜尋結果）</CardTitle>
              </CardHeader>
              <CardContent>
                {aliPreviewLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : aliPreview.length === 0 ? (
                  <div className="flex items-center justify-center py-8 text-muted-foreground">
                    <AlertCircle className="h-5 w-5 mr-2" />
                    無資料
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">#</TableHead>
                          <TableHead>Title 標題</TableHead>
                          <TableHead>Price 價格</TableHead>
                          <TableHead>MOQ</TableHead>
                          <TableHead>Supplier 供應商</TableHead>
                          <TableHead className="w-20">TA</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {aliPreview.map((p, i) => (
                          <TableRow key={p.id}>
                            <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                            <TableCell className="max-w-[300px]">
                              <div className="line-clamp-1 font-medium">{p.title || '—'}</div>
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-primary font-medium">
                              {p.price_current || '—'}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">{p.moq_text || '—'}</TableCell>
                            <TableCell>
                              <div className="text-sm">{p.company || '—'}</div>
                              {p.years_in_business && p.years_in_business > 0 && (
                                <div className="text-xs text-muted-foreground">{p.years_in_business} yrs</div>
                              )}
                            </TableCell>
                            <TableCell>
                              {p.has_trade_assurance ? (
                                <Badge variant="default" className="bg-green-500 text-white text-[10px]">TA</Badge>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* ── Google Sheet 自動同步綁定 ── */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Link2 className="h-4 w-4 text-[#0F9D58]" />
                Google Sheet 自動同步綁定 Auto-Sync Binding
              </CardTitle>
              <CardDescription>
                將關鍵字資料綁定到現有 Google Sheet，定時自動更新。每行末尾附 <code>update_log</code> 欄位紀錄同步裝置與時間。
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Binding form */}
              {!sheetsAuthorized ? (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-muted text-sm text-muted-foreground">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  請先點上方「連結 Google Sheets」授權，再設定自動同步綁定。
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-[1fr_180px_120px_auto]">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Google Sheet URL</Label>
                    <Input
                      placeholder="https://docs.google.com/spreadsheets/d/..."
                      value={bindingUrl}
                      onChange={e => setBindingUrl(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">關鍵字 Keyword</Label>
                    <Select value={bindingKeyword} onValueChange={setBindingKeyword}>
                      <SelectTrigger>
                        <SelectValue placeholder="選擇關鍵字" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">全部關鍵字 (all)</SelectItem>
                        {aliKeywords.map(k => (
                          <SelectItem key={k} value={k}>{k}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">同步頻率 Interval</Label>
                    <Select value={bindingInterval} onValueChange={setBindingInterval}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">每小時</SelectItem>
                        <SelectItem value="6">每 6 小時</SelectItem>
                        <SelectItem value="12">每 12 小時</SelectItem>
                        <SelectItem value="24">每天</SelectItem>
                        <SelectItem value="168">每週</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={handleBindSheet}
                      disabled={bindingLoading || !bindingUrl || !bindingKeyword}
                      className="bg-[#0F9D58] hover:bg-[#0B8043] text-white whitespace-nowrap"
                    >
                      {bindingLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
                      綁定並立即同步
                    </Button>
                  </div>
                </div>
              )}

              {/* Bindings list */}
              {bindings.length > 0 && (
                <div className="border rounded-lg overflow-hidden mt-2">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>關鍵字</TableHead>
                        <TableHead>Sheet</TableHead>
                        <TableHead>Tab</TableHead>
                        <TableHead className="text-center">頻率</TableHead>
                        <TableHead>上次同步</TableHead>
                        <TableHead className="text-right">操作</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bindings.map(b => (
                        <TableRow key={b.id}>
                          <TableCell>
                            <Badge variant="outline" className="font-mono text-xs">
                              {b.keyword || '全部'}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-[200px]">
                            <a
                              href={b.sheet_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-xs text-blue-600 hover:underline truncate"
                            >
                              <ExternalLink className="h-3 w-3 shrink-0" />
                              {b.sheet_id}
                            </a>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">{b.tab_name}</TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              {b.sync_interval_hours}h
                            </div>
                          </TableCell>
                          <TableCell className="text-xs">
                            {b.last_sync_error ? (
                              <div className="text-red-500 flex items-center gap-1">
                                <XCircle className="h-3 w-3" /> 失敗
                              </div>
                            ) : b.last_synced_at ? (
                              <div className="space-y-0.5">
                                <div className="text-green-600 flex items-center gap-1">
                                  <CheckCircle2 className="h-3 w-3" />
                                  {b.last_sync_rows ?? 0} 筆
                                </div>
                                <div className="text-muted-foreground text-[11px]">
                                  {b.last_sync_device && `${b.last_sync_device} · `}
                                  {new Date(b.last_synced_at).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">未同步</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2"
                                disabled={syncBindingMut.isPending}
                                onClick={() => syncBindingMut.mutate(b.id)}
                                title="立即同步"
                              >
                                {syncBindingMut.isPending ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <RefreshCw className="h-3 w-3" />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-red-500 hover:text-red-600 hover:bg-red-50"
                                disabled={deleteBindingMut.isPending}
                                onClick={() => { if (confirm(`確認刪除「${b.keyword || '全部'}」的綁定？`)) deleteBindingMut.mutate(b.id); }}
                                title="刪除綁定"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {bindings.length === 0 && sheetsAuthorized && (
                <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                  目前沒有綁定，填入上方表單建立第一個自動同步。
                </div>
              )}
            </CardContent>
          </Card>

        </TabsContent>

        {/* ── SERP Analysis Report Export Tab ── */}
        <TabsContent value="analysis" className="space-y-4">

          {/* Project Selection (shared selectedProjectId) */}
          <Card>
            <CardHeader>
              <CardTitle>SERP 分析報告匯出</CardTitle>
              <CardDescription>
                匯出已完成 Step 3～6 分析的關鍵字，每列一個關鍵字，共 40 欄（競爭者 / 意圖 / 機會 / 大綱）。
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">

              {/* Project selector (reuses SERP tab's selectedProjectId) */}
              <div className="space-y-2">
                <Label>專案</Label>
                <Select
                  value={selectedProjectId}
                  onValueChange={(value) => {
                    setSelectedProjectId(value);
                    setSelectedKeywords(new Set());
                  }}
                >
                  <SelectTrigger className="max-w-xs">
                    <SelectValue placeholder="選擇專案" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.length === 0 ? (
                      <SelectItem value="__none__" disabled>尚無專案</SelectItem>
                    ) : (
                      projects.map(p => (
                        <SelectItem key={p.project_id} value={p.project_id}>
                          {p.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Statistics */}
              {selectedProjectId && (
                <div className="flex gap-4 text-sm">
                  <span>共 <b>{analysisKeywords.length}</b> 筆</span>
                  <span>已完成 4 步驟：<b>{analysisKeywords.filter(k => k.steps_completed >= 4).length}</b> 筆</span>
                </div>
              )}

              {/* Keyword list preview */}
              {selectedProjectId && (
                analysisLoading ? (
                  <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    載入中...
                  </div>
                ) : (
                  <div className="border rounded-md overflow-auto max-h-64">
                    <Table>
                      <TableHeader className="sticky top-0 bg-muted">
                        <TableRow>
                          <TableHead className="text-left">關鍵字</TableHead>
                          <TableHead className="text-center w-28">完成步驟</TableHead>
                          <TableHead className="text-left w-32">建立時間</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {analysisKeywords.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} className="text-center py-6 text-muted-foreground">
                              此專案尚無完成的分析關鍵字
                            </TableCell>
                          </TableRow>
                        ) : (
                          analysisKeywords.map((kw, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{kw.keyword}</TableCell>
                              <TableCell className="text-center">
                                <Badge
                                  variant={kw.steps_completed >= 4 ? "default" : "secondary"}
                                  className={kw.steps_completed >= 4 ? "bg-green-500 text-white" : "bg-yellow-100 text-yellow-700 border-yellow-300"}
                                >
                                  {kw.steps_completed}/{kw.steps_total}
                                  {kw.steps_completed >= 4 ? ' ✓' : ' ⚠'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-muted-foreground text-xs">
                                {kw.created_at ? new Date(kw.created_at).toLocaleDateString('zh-TW') : '—'}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )
              )}

              {!selectedProjectId && (
                <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
                  <AlertCircle className="h-4 w-4" />
                  請先選擇專案
                </div>
              )}

              {/* Filter option */}
              <div className="flex items-center gap-2">
                <Switch
                  id="requireAllSteps"
                  checked={requireAllSteps}
                  onCheckedChange={setRequireAllSteps}
                />
                <Label htmlFor="requireAllSteps" className="text-sm cursor-pointer">
                  僅匯出 4 步驟完成的關鍵字
                </Label>
              </div>

              {/* Format + Download */}
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex gap-2">
                  {(['excel', 'json'] as const).map(fmt => (
                    <Button
                      key={fmt}
                      variant={analysisFormat === fmt ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setAnalysisFormat(fmt)}
                    >
                      {fmt === 'excel' ? (
                        <FileSpreadsheet className="h-3.5 w-3.5 mr-1" />
                      ) : (
                        <FileJson className="h-3.5 w-3.5 mr-1" />
                      )}
                      {fmt.toUpperCase()}
                    </Button>
                  ))}
                </div>
                <Button
                  onClick={handleAnalysisExport}
                  disabled={!selectedProjectId || analysisExporting}
                  size="lg"
                >
                  {analysisExporting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : analysisFormat === 'excel' ? (
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                  ) : (
                    <FileJson className="h-4 w-4 mr-2" />
                  )}
                  下載分析報告 {analysisFormat.toUpperCase()}
                </Button>
              </div>

              {/* Google Sheets Export */}
              <div className="border-t pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium flex items-center gap-1.5">
                    <FileSpreadsheet className="h-4 w-4 text-green-600" />
                    匯出到 Google 資料表
                  </Label>
                  {sheetsAuthorized ? (
                    <Badge variant="outline" className="text-green-600 border-green-300 gap-1 pr-1">
                      <CheckCircle2 className="h-3 w-3" /> 已連結
                      <button
                        className="ml-1 text-xs text-muted-foreground hover:text-destructive"
                        onClick={() => setSheetsAuthorized(false)}
                      >✕</button>
                    </Badge>
                  ) : (
                    <Button variant="outline" size="sm" onClick={handleSheetsAuth}>
                      <ExternalLink className="h-3.5 w-3.5 mr-1" /> 連結 Google Sheets
                    </Button>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Google Sheets 網址</Label>
                  <Input
                    placeholder="https://docs.google.com/spreadsheets/d/..."
                    value={analysisSheetUrl}
                    onChange={e => setAnalysisSheetUrl(e.target.value)}
                    disabled={!sheetsAuthorized}
                  />
                </div>

                <div className="flex items-end gap-3">
                  <div className="space-y-2 flex-1 max-w-48">
                    <Label className="text-xs text-muted-foreground">目標分頁名稱</Label>
                    <Input
                      placeholder="SERP Analysis"
                      value={analysisSheetTab}
                      onChange={e => setAnalysisSheetTab(e.target.value)}
                      disabled={!sheetsAuthorized}
                    />
                  </div>
                  <Button
                    onClick={handleExportAnalysisToSheets}
                    disabled={!sheetsAuthorized || !selectedProjectId || !analysisSheetUrl.trim() || analysisSheetExporting}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {analysisSheetExporting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                    )}
                    寫入 Google 資料表
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground">
                  將 {requireAllSteps ? '已完成 4 步驟的' : '所有'} 關鍵字分析（40 欄）寫入指定分頁；分頁不存在時自動建立。
                </p>
              </div>

            </CardContent>
          </Card>

        </TabsContent>
      </Tabs>
    </div>
  );
}
